import React, { FC, ReactElement } from 'react';
import classNames from 'classnames';
import { useApolloClient } from '@apollo/client';

import styles from './returnPerformanceStyles.scss';

import HoverOverTooltip from '@buyside/components/presentation/Tooltips/HoverOverTooltip/hoverOverTooltip';
import InfoTable from '@buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import ReturnPerformanceLoading from '@buyside/components/presentation/IdeaShowPage/RightSidebar/ReturnPerformance/returnPerformanceLoading';
import ReturnValue from '@buyside/components/presentation/ReturnValue/returnValue';

import {
  IdeaCoreDocument,
  IdeaCoreQuery,
  IdeaPositionsDocument,
  IdeaPositionsQuery,
  Maybe
} from '@interfaces/graphql-typings';
enum ReturnPerformanceLabels {
  RealizedReturn = 'REALIZED RETURN',
  ReturnToDate = 'RETURN TO DATE',
  ExpectedReturn = 'EXPECTED RETURN',
  IRR = 'IRR',
  ReturnVsBenchmark = 'RETURN VS. BENCHMARK'
}

const createReturnPerformanceItems = (
  closedAt: Maybe<string>,
  isPairTrade: boolean,
  positions: IdeaPositionsQuery['idea']['positions'],
  totalReturn: Maybe<number>,
  annualizedReturn?: Maybe<number>,
  benchmarkNames?: Maybe<string>,
  expectedReturn?: Maybe<number>,
  returnVsBenchmark?: Maybe<number>
): {
  label: ReturnPerformanceLabels | JSX.Element;
  data: string | JSX.Element;
  labelType: ReturnPerformanceLabels;
}[] =>
  [
    {
      label: closedAt
        ? ReturnPerformanceLabels.RealizedReturn
        : ReturnPerformanceLabels.ReturnToDate,
      data: isPairTrade ? (
        <HoverOverTooltip
          overlay={
            <div>
              {positions.map(
                ({ id, asset, totalReturn }): ReactElement => (
                  <div key={id}>
                    {asset?.symbol}: {totalReturn ? `${totalReturn.toFixed(2)}%` : 'N/A'}
                  </div>
                )
              )}
            </div>
          }
        >
          <ReturnValue value={totalReturn} />
        </HoverOverTooltip>
      ) : (
        <ReturnValue value={totalReturn} />
      ),
      labelType: closedAt
        ? ReturnPerformanceLabels.RealizedReturn
        : ReturnPerformanceLabels.ReturnToDate
    },
    closedAt
      ? null
      : {
          label: ReturnPerformanceLabels.ExpectedReturn,
          data: (
            <HoverOverTooltip
              overlay={
                <span>
                  <strong>Remaining</strong> return that is expected given the current price/return
                  and the target price/return.
                </span>
              }
            >
              {expectedReturn ? `${expectedReturn.toFixed(2)}%` : 'N/A'}
            </HoverOverTooltip>
          ),
          labelType: ReturnPerformanceLabels.ExpectedReturn
        },
    {
      label: ReturnPerformanceLabels.IRR,
      data: (
        <HoverOverTooltip
          overlay={
            <span>
              The IRR calculation is displayed once the recommendation has been active for more than
              30 days
            </span>
          }
        >
          {annualizedReturn ? `${annualizedReturn.toFixed(2)}%` : 'N/A'}
        </HoverOverTooltip>
      ),
      labelType: ReturnPerformanceLabels.IRR
    },
    {
      label: (
        <>
          {ReturnPerformanceLabels.ReturnVsBenchmark}
          <div>{benchmarkNames ? `(${benchmarkNames.toUpperCase()})` : null}</div>
        </>
      ),
      data: isPairTrade ? (
        <HoverOverTooltip
          overlay={
            <div>
              The return of the idea relative to the overall benchmark performance, which is
              calculated by weighting the individual benchmarks' performances according to the
              idea's pair ratio.
            </div>
          }
        >
          {returnVsBenchmark ? `${returnVsBenchmark.toFixed(2)}%` : 'N/A'}
        </HoverOverTooltip>
      ) : (
        <div>{returnVsBenchmark ? `${returnVsBenchmark.toFixed(2)}%` : 'N/A'}</div>
      ),
      labelType: ReturnPerformanceLabels.ReturnVsBenchmark
    }
  ].filter(Boolean) as {
    label: ReturnPerformanceLabels | JSX.Element;
    data: string | JSX.Element;
    labelType: ReturnPerformanceLabels;
  }[];

const ReturnPerformance: FC<{ ideaId: string; loading: boolean }> = ({ ideaId, loading }) => {
  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCoreDocument,
    variables: { ideaId }
  })?.idea;

  const cachedPosIdea = client.readQuery<IdeaPositionsQuery>({
    query: IdeaPositionsDocument,
    variables: { ideaId }
  })?.idea;

  if (loading || !cachedIdea || !cachedPosIdea) return <ReturnPerformanceLoading />;

  const { positions } = cachedPosIdea;
  const {
    annualizedReturn,
    benchmarkNames,
    canCurrentUserViewIdeaFilterDetails: canViewIdeaFilterDetails,
    closedAt,
    expectedReturn,
    returnVsBenchmark,
    totalReturn
  } = cachedIdea;

  const isPairTrade = positions.length > 1;

  return (
    <InfoTable title="Return Performance">
      {({ labelStyle, dataStyle, blur }): ReactElement[] => {
        const ideaFilterDetails = [ReturnPerformanceLabels.ExpectedReturn];

        return createReturnPerformanceItems(
          closedAt,
          isPairTrade,
          positions,
          totalReturn,
          annualizedReturn,
          benchmarkNames,
          expectedReturn,
          returnVsBenchmark
        ).map(
          ({ label, data, labelType }, idx): ReactElement => (
            <div key={idx} className={styles.tableRow}>
              <div className={labelStyle}>{label}</div>
              <div
                data-testid={labelType
                  .toString()
                  .replace(/ /g, '-')
                  .toLowerCase()}
                className={classNames(dataStyle, {
                  [blur]: ideaFilterDetails.includes(labelType) && !canViewIdeaFilterDetails
                })}
              >
                {data}
              </div>
            </div>
          )
        );
      }}
    </InfoTable>
  );
};

export default ReturnPerformance;
